import React, {
  Children,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import PropTypes from "kf-proptypes/widgets";
import classNames from "classnames";
import { Show } from "control-flow-react";
import { t } from "@lingui/macro";

import { Button, BUTTON_TYPES, BUTTON_SIZES } from "@widgets/inputs/index.jsx";
import {
  ICON_SIZE,
  ICON_STROKE,
  KF_ICONS,
  KfIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import styles from "./styles.css";

const StepperContext = createContext();

export const STEPPER_TYPE = {
  DEFAULT: "default",
  OUTLINE_STROKED: "outlinestroked"
};

export const STEPPER_SIZE = {
  LARGE: "large",
  BASE: "base",
  MEDIUM: "medium"
};

/**
 * It is processes by breaking them into logical and manageable chunks, reducing user confusion, and providing a clear path to completion
 */
export const Stepper = forwardRef((props, ref) => {
  const {
    initialStep = 0,
    checkedIcon,
    onChangeActive = () => {},
    children,
    errorIcon,
    size = STEPPER_SIZE.MEDIUM,
    type = STEPPER_TYPE.DEFAULT
  } = props;

  const childrenArray = Children.toArray(children);

  const [activePanel, setActivePanel] = useState(initialStep);

  const [visitedStep, setVisitedStep] = useState(0);

  const panelProps = {};
  const stepperPanel = [];

  useImperativeHandle(ref, () => ({
    setVisitedStep: (step) => {
      setActivePanel(step);
      setVisitedStep(step);
      onChangeActive(step);
    },
    setAssociatedState: (activePanel, visitedStep) => {
      setActivePanel(activePanel);
      visitedStep && setVisitedStep(visitedStep);
      onChangeActive(activePanel);
    }
  }));

  useEffect(
    function updateActivePanel() {
      setActivePanel(initialStep);
    },
    [initialStep]
  );

  useEffect(
    function updateActivePanel() {
      if (visitedStep < activePanel) {
        setVisitedStep(activePanel);
        onChangeActive(activePanel);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePanel]
  );

  function goToPrevious() {
    setActivePanel((prev) => prev - 1);
  }

  function goToNext() {
    setActivePanel((prev) => prev + 1);
  }

  function goTo(activePanel) {
    setActivePanel(activePanel);
  }

  childrenArray.forEach((child, panelId) => {
    if (child.type === StepperPanel) {
      panelProps[panelId] = {
        id: panelId,
        isOpen: !child.props.disabled && activePanel === panelId,
        checkedIcon: checkedIcon || (
          <KfIcon
            icon={KF_ICONS.SELECT_ROUND}
            size={ICON_SIZE.SIZE_16}
            stroke={ICON_STROKE.BOLD}
            color="--kf-color-info-500"
          />
        ),
        isCompleted: !child.props.disabled && visitedStep > panelId,
        isActive: activePanel === panelId,
        disabledPanel: visitedStep < panelId,
        onChange: (panelId) => {
          setActivePanel((previous) => (previous !== panelId ? panelId : null));
        },
        goToPrevious,
        goToNext,
        goTo,
        size
      };

      if (errorIcon) {
        panelProps[panelId]["errorIcon"] = errorIcon;
      }

      if (!child.props.footerRenderer) {
        panelProps[panelId]["hidePreviousNavigation"] = panelId === 0;
      }

      stepperPanel.push({
        ...child,
        props: { ...child.props, panelId: panelId }
      });
    }
  });

  return (
    <div
      className={`${styles.stepperContainer} ${styles[type]}`}
      aria-label="Stepper"
    >
      <StepperContext.Provider value={panelProps}>
        {stepperPanel}
      </StepperContext.Provider>
    </div>
  );
});

const StepperPanel = forwardRef((props, ref) => {
  const {
    children,
    title,
    onNext = () => {},
    onPrevious = () => {},
    nextButtonConfig = {},
    previousButtonConfig = {},
    footerRenderer = () => {},
    rightRenderer,
    lazyLoad = false,
    error = false,
    disabled = false,
    panelId,
    customIcon,
    customFooterStyle
  } = props;

  const contextData = useContext(StepperContext);

  const contentRef = useRef(null);

  const {
    isOpen,
    checkedIcon,
    isCompleted,
    disabledPanel,
    hidePreviousNavigation,
    onChange,
    goToPrevious,
    goToNext,
    goTo,
    isActive,
    size,
    errorIcon
  } = contextData[panelId];

  const disabledState = disabled || disabledPanel;

  const [isContentVisible, setIsContentVisible] = useState(false);

  useImperativeHandle(ref, () => ({
    goToNext
  }));

  useEffect(
    function showPanel() {
      if (isOpen && lazyLoad) {
        setIsContentVisible(true);
      }

      if (isOpen) {
        contentRef.current.style.display = "block";
      }
    },
    [isOpen, lazyLoad]
  );

  function handleTransitionEnd(event) {
    if (!isOpen && event.propertyName === "visibility") {
      contentRef.current.style.display = "none";
      if (lazyLoad) setIsContentVisible(false);
    }
  }

  function handlePanelClick() {
    if (disabledState) return;
    onChange(panelId);
  }

  function getTabIndex(disabled) {
    if (disabled) return "-1";
    return "0";
  }

  const stepperIconClassName = classNames("stepper-icon", {
    "stepper-icon-open": isOpen
  });

  const stepperClassName = classNames("stepper-panel", {
    "stepper-open": isOpen,
    "stepper-disabled": disabledState
  });

  const stepperPanelCountClassName = classNames(
    "micro",
    "stepper-panel-count",
    {
      "stepper-panel-count-active": isActive && !disabledState
    }
  );

  const tabIndex = getTabIndex(disabledState);

  return (
    <div
      className={`${stepperClassName} ${styles[size]}`}
      aria-label="Stepper Panel"
    >
      <div
        className="stepper-panel-header"
        aria-disabled={disabledState}
        aria-label="Stepper Header"
        tabIndex={tabIndex}
        onClick={handlePanelClick}
      >
        <Show
          when={!error || disabledState}
          fallBack={
            errorIcon ? (
              errorIcon
            ) : (
              <KfIcon
                icon={KF_ICONS.INFO_WITH_CIRCLE}
                size={ICON_SIZE.SIZE_16}
                stroke={ICON_STROKE.BOLD}
                color="--kf-color-error-500"
              />
            )
          }
        >
          <Show
            when={!disabledState && isCompleted}
            fallBack={
              customIcon ? (
                <div
                  className={`${disabledState ? styles.disabledIcon : ""} ${
                    styles.customPanelIcon
                  }`}
                >
                  {customIcon}
                </div>
              ) : (
                <div className={stepperPanelCountClassName}>{panelId + 1}</div>
              )
            }
          >
            {checkedIcon}
          </Show>
        </Show>

        <div className="base stepper-panel-header-title">{title}</div>
        <div className="stepper-panel-header-rightrenderer">
          <Show when={!disabledState}>{rightRenderer}</Show>
        </div>

        <div className={stepperIconClassName}>
          <KfIcon
            icon={KF_ICONS.ARROW_RIGHT_SIMPLE}
            size={ICON_SIZE.SIZE_16}
            stroke={ICON_STROKE.REGULAR}
            color={
              disabledState ? "--kf-color-gray-400" : "--kf-color-gray-700"
            }
          />
        </div>
      </div>

      <div className="stepper-body-wrapper">
        <div onTransitionEnd={handleTransitionEnd} className="stepper-body">
          <div ref={contentRef} className="stepper-body-content">
            <Show when={lazyLoad ? isContentVisible : true}>{children}</Show>
            <div className={`stepper-panel-footer ${customFooterStyle}`}>
              {footerRenderer({ goToPrevious, goToNext, goTo }) || (
                <DefaultFooterRenderer
                  hidePreviousNavigation={hidePreviousNavigation}
                  onNext={() => onNext({ goToNext, goTo })}
                  onPrevious={() => onPrevious({ goToPrevious, goTo })}
                  nextButtonConfig={nextButtonConfig}
                  previousButtonConfig={previousButtonConfig}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

function DefaultFooterRenderer(props) {
  const {
    hidePreviousNavigation,
    onNext,
    onPrevious,
    nextButtonConfig,
    previousButtonConfig
  } = props;
  return (
    <div className="stepper-panel-footerrenderer">
      {!hidePreviousNavigation && (
        <Button
          type={BUTTON_TYPES.SECONDARY}
          size={BUTTON_SIZES.SMALL_DEPRECATED}
          onClick={onPrevious}
          {...previousButtonConfig}
        >
          {previousButtonConfig.text || t`Previous`}
        </Button>
      )}

      <Button
        type={BUTTON_TYPES.PRIMARY}
        size={BUTTON_SIZES.SMALL_DEPRECATED}
        onClick={onNext}
        {...nextButtonConfig}
      >
        {nextButtonConfig.text || t`Next`}
      </Button>
    </div>
  );
}

DefaultFooterRenderer.propTypes = {
  hidePreviousNavigation: PropTypes.bool,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  nextButtonConfig: PropTypes.shape({ text: PropTypes.string }),
  previousButtonConfig: PropTypes.shape({ text: PropTypes.string })
};

Stepper.Panel = StepperPanel;

Stepper.propTypes = {
  /** It is initial active step. by default it is 0   */
  initialStep: PropTypes.number,
  /** Custom Icon for Panel completed state  */
  checkedIcon: PropTypes.element,
  /** return the activated step   */
  onChangeActive: PropTypes.func,
  /** Children of Stepper must be <Stepper.Panel/>  */
  children: PropTypes.element,

  /** Size of the Stepper. by default it will be medium */
  size: PropTypes.oneOf(Object.values(STEPPER_SIZE)),
  /** Type of the stepper. by default it will be default*/
  type: PropTypes.oneOf(Object.values(STEPPER_TYPE)),
  /** Custom icon for error state */
  errorIcon: PropTypes.element
};

StepperPanel.propTypes = {
  /** Content of the step*/
  children: PropTypes.element,
  /** Title for the panel*/
  title: PropTypes.string,
  /** Disable state of the Panel*/
  disabled: PropTypes.bool,
  /** Handles the Next button click handler (Didn't work if footerRenderer given)*/
  onNext: PropTypes.func,
  /** Handles the Previous button click handler (Didn't work if footerRenderer given)*/
  onPrevious: PropTypes.func,
  /** props for Next button*/
  nextButtonConfig: PropTypes.shape({ text: PropTypes.string }),
  /** props for Previous button  */
  previousButtonConfig: PropTypes.shape({ text: PropTypes.string }),
  /** If custom Footer needed it will given in this */
  footerRenderer: PropTypes.func,
  /** Right renderer will render in the right side of header*/
  rightRenderer: PropTypes.element,
  /** Lazy rendering of content*/
  lazyLoad: PropTypes.bool,
  /** Error state of Panel*/
  error: PropTypes.bool,
  /** panelId is auto generated.*/
  panelId: PropTypes.number,
  /** size of the Panel */
  size: PropTypes.oneOf(Object.values(STEPPER_SIZE)),
  /** To override stepper panel footer */
  customFooterStyle: PropTypes.string,
  /** Custom Icon for Panel */
  customIcon: PropTypes.element
};
